import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

// A.5.4 lokale Helper - bewusst nicht in utils ausgelagert (eigener
// Refactor-Schritt, falls die gleiche Logik an noch mehr Stellen
// gebraucht wird). Spiegelt das Verhalten von BautagebuchForm.js wider.
const STUNDEN_TYPEN = ['regulaer', 'regie', 'zusatz'];
const TYP_LABELS = { regulaer: 'Regulär', regie: 'Regie', zusatz: 'Zusatz' };
const typBadgeClass = (t) => t === 'regie' ? 'amber' : t === 'zusatz' ? 'blue' : 'grey';

const isMigrationsZeile = (row) => typeof row?.bemerkung === 'string'
  && row.bemerkung.startsWith('Migriert aus alter pauschaler');

const calcAggregat = (rows) => {
  let summe = 0;
  const distinctUsers = new Set();
  let nullCount = 0;
  for (const r of rows) {
    const n = Number(r.stunden);
    if (!Number.isFinite(n) || n <= 0) continue;
    summe += n;
    if (r.user_id == null) nullCount += 1;
    else distinctUsers.add(r.user_id);
  }
  return { summe, personen: distinctUsers.size + nullCount };
};

const sumByTyp = (rows, typ) => rows
  .filter(r => r.typ === typ)
  .reduce((s, r) => {
    const n = Number(r.stunden);
    return s + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);

const fmtNum = (n) => (n == null || !Number.isFinite(Number(n))) ? '–' : Number(n).toFixed(2);

export default function BautagebuchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [stunden, setStunden] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // A.5.4: BT-Header und Stundenzeilen parallel laden.
    Promise.all([
      api.get(`/bautagebuecher/${id}`),
      api.get(`/bautagebuecher/${id}/stunden`),
    ]).then(([rBT, rSt]) => {
      setB(rBT.data);
      setFotos(rBT.data.fotos || []);
      setStunden(Array.isArray(rSt.data) ? rSt.data : []);
    }).catch(() => navigate('/bautagebuch'));
  }, [id]);

  const handleFotos = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('fotos', f);
      fd.append('bautagebuch_id', id);
      const r = await api.post('/fotos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFotos(prev => [...prev, ...r.data]);
    } catch { alert('Upload fehlgeschlagen'); }
    finally { setUploading(false); }
  };

  const deleteFoto = async (fotoId) => {
    if (!window.confirm('Foto löschen?')) return;
    await api.delete(`/fotos/${fotoId}`).catch(() => {});
    setFotos(prev => prev.filter(f => f.id !== fotoId));
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    window.open(`/api/bautagebuecher/${id}/pdf?token=${token}`, '_blank');
  };

  if (!b) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Bautagebuch #{id}</div>
          <div className="page-sub">{b.datum ? new Date(b.datum).toLocaleDateString('de-DE') : ''} · {b.baustelle_name || '–'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>← Zurück</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/bautagebuch/${id}/bearbeiten`)}>✏️ Bearbeiten</button>
          <button className="btn btn-primary" onClick={exportPdf}>📄 PDF exportieren</button>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Status</div>
          <div className="stat-value" style={{ fontSize: 16, marginTop: 8 }}>
            {b.status === 'fertig' ? <span className="badge badge-copper">Fertig</span> : <span className="badge badge-amber">Entwurf</span>}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Stunden</div>
          <div className="stat-value text-copper">{b.gesamtstunden || '–'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Monteure</div>
          <div className="stat-value">{b.anzahl_monteure || '–'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Fotos</div>
          <div className="stat-value">{fotos.length}</div>
        </div>
      </div>

      <div className="card card-accent">
        <div className="card-title" style={{ marginBottom: 14 }}>Kopfdaten</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {[['Datum', b.datum ? new Date(b.datum).toLocaleDateString('de-DE') : '–'], ['Baustelle', b.baustelle_name||'–'], ['Bauleiter', b.bauleiter||'–'], ['Erstellt von', b.user_name||'–']].map(([l,v]) => (
            <div key={l} style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2, fontWeight: 600 }}>{l}</div>
              <div style={{ fontWeight: 500 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {(b.temperatur || b.wetter) && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Witterungsbedingungen</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {[['Temperatur', b.temperatur], ['Wetter', b.wetter], ['Wind', b.wind], ['Niederschlag', b.niederschlag], ['Sichtweite', b.sichtweite], ['Bemerkung', b.wetter_bem]].filter(([,v]) => v).map(([l,v]) => (
              <div key={l} style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2, fontWeight: 600 }}>{l}</div>
                <div style={{ fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {b.material?.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Materialeinlage</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Pos.-Nr.</th><th>Bezeichnung</th><th>Einh.</th><th>Menge</th><th>Lage</th><th>Bemerkung</th></tr></thead>
              <tbody>
                {(() => {
                  // M2.3: Auto-Zeilen (server-generierter Lagerabzug aus dem
                  // Positions-Rezept, M2) direkt unter ihrer Quell-Zeile
                  // einsortieren und klar kennzeichnen.
                  const rows = b.material.filter(m => m.menge || m.lage);
                  const manuell = rows.filter(m => !m.auto_generiert);
                  const autos = rows.filter(m => m.auto_generiert);
                  const sortiert = [];
                  for (const m of manuell) {
                    sortiert.push(m);
                    for (const a of autos) {
                      if (a.auto_quelle_einlage_id === m.id) sortiert.push(a);
                    }
                  }
                  for (const a of autos) {
                    if (!sortiert.includes(a)) sortiert.push(a);
                  }
                  const quelleVon = (a) => {
                    const q = b.material.find(x => x.id === a.auto_quelle_einlage_id);
                    return q ? (q.pos_nr || q.bezeichnung) : null;
                  };
                  return sortiert.map((m, i) => (
                    <tr key={m.id ?? i} style={m.auto_generiert ? { opacity: 0.85 } : undefined}>
                      <td className="text-mono text-copper" style={{ fontWeight: 600 }}>{m.pos_nr}</td>
                      <td style={m.auto_generiert ? { paddingLeft: 22 } : undefined}>
                        {m.auto_generiert && <span style={{ color: 'var(--text-3)', marginRight: 4 }}>↳</span>}
                        {m.bezeichnung}
                        {m.projekt_position_id && <span className="badge badge-copper" style={{ marginLeft: 8, fontSize: 9 }}>Projekt</span>}
                        {m.auto_generiert && (
                          <span className="badge badge-blue" style={{ marginLeft: 8, fontSize: 9 }}>
                            ⚙ automatisch{quelleVon(m) ? ` · aus Pos. ${quelleVon(m)}` : ''}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>{m.einheit}</td>
                      <td style={{ fontWeight: 600 }}>{m.menge}</td>
                      <td>{m.lage}</td>
                      <td>{m.bemerkung}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* A.5.4: Read-only Anzeige der Pro-Person-Stundenzeilen. */}
      {(() => {
        const agg = calcAggregat(stunden);
        const typTotals = STUNDEN_TYPEN
          .map(t => ({ t, sum: sumByTyp(stunden, t) }))
          .filter(x => x.sum > 0);
        const hasMigrationsZeile = stunden.some(isMigrationsZeile);
        const personenLabel = agg.personen === 1 ? 'Person' : 'Personen';

        return (
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Stunden pro Mitarbeiter</div>
                <div className="card-sub" style={{ fontSize: 12 }}>
                  {stunden.length === 0
                    ? 'Keine Stundenzeilen erfasst'
                    : (
                      <>
                        {agg.personen} {personenLabel} · {fmtNum(agg.summe)} h gesamt
                        {typTotals.length > 0 && (
                          <>
                            {' ('}
                            {typTotals.map((x, i) => (
                              <span key={x.t}>
                                {i > 0 ? ' · ' : ''}{fmtNum(x.sum)} h {TYP_LABELS[x.t]}
                              </span>
                            ))}
                            {')'}
                          </>
                        )}
                      </>
                    )}
                </div>
                {hasMigrationsZeile && (
                  <div style={{
                    fontSize: 11,
                    color: 'var(--text-3)',
                    marginTop: 4,
                    fontStyle: 'italic',
                  }}>
                    Aus alter Sammelerfassung übernommen
                  </div>
                )}
              </div>
            </div>

            {stunden.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">👷</div>
                <h3>Keine Stundenzeilen</h3>
                <p>Für dieses Bautagebuch sind keine Pro-Person-Stundenzeilen erfasst.</p>
              </div>
            ) : (
              <>
                {/* Desktop: kompakte Tabelle */}
                <div className="table-wrap desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>Mitarbeiter</th>
                        <th>Funktion</th>
                        <th>Stunden</th>
                        <th>Typ</th>
                        <th>Abrechenbar</th>
                        <th>Position</th>
                        <th>Bemerkung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stunden.map(s => {
                        const isMig = isMigrationsZeile(s);
                        const isExtern = s.user_id == null;
                        const positionText = s.projekt_position_id
                          ? `${s.projekt_position_pos_nr ? s.projekt_position_pos_nr + ' – ' : ''}${s.projekt_position_bezeichnung || ''}`
                          : '';
                        return (
                          <tr key={s.id}>
                            <td>
                              {isExtern
                                ? (s.subunternehmer_firma
                                    // A.7: Firma aus Stammdaten; Legacy-/Freitext-Zeilen
                                    // (ohne Zuordnung) behalten die alte Darstellung.
                                    ? <>
                                        {s.subunternehmer_firma}
                                        <span className="badge badge-grey" style={{ marginLeft: 6, fontSize: 9 }}>Subunternehmer</span>
                                      </>
                                    : <em style={{ color: 'var(--text-3)' }}>Extern / Subunternehmer</em>)
                                : (s.user_name || s.user_username || '–')}
                              {isMig && (
                                <span className="badge badge-grey" style={{ marginLeft: 6, fontSize: 9 }}>Migration</span>
                              )}
                            </td>
                            <td>{s.rolle_bezeichnung || '–'}</td>
                            <td className="text-mono" style={{ fontWeight: 600 }}>{fmtNum(s.stunden)} h</td>
                            <td>
                              <span className={`badge badge-${typBadgeClass(s.typ)}`}>
                                {TYP_LABELS[s.typ] || s.typ}
                              </span>
                            </td>
                            <td>
                              {s.abrechenbar
                                ? <span className="badge badge-green">ja</span>
                                : <span className="badge badge-grey">nein</span>}
                            </td>
                            <td>{positionText || '–'}</td>
                            <td>{s.bemerkung || '–'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Karten-Layout pro Zeile */}
                <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {stunden.map(s => {
                    const isMig = isMigrationsZeile(s);
                    const isExtern = s.user_id == null;
                    const positionText = s.projekt_position_id
                      ? `${s.projekt_position_pos_nr ? s.projekt_position_pos_nr + ' – ' : ''}${s.projekt_position_bezeichnung || ''}`
                      : '';
                    return (
                      <div key={s.id} style={{
                        background: 'var(--bg-elevated)',
                        borderRadius: 'var(--radius)',
                        padding: 12,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                          <div style={{ fontWeight: 600, minWidth: 0 }}>
                            {isExtern
                              ? (s.subunternehmer_firma
                                  ? <>
                                      {s.subunternehmer_firma}
                                      <span className="badge badge-grey" style={{ marginLeft: 6, fontSize: 9 }}>Subunternehmer</span>
                                    </>
                                  : <em style={{ color: 'var(--text-3)' }}>Extern / Subunternehmer</em>)
                              : (s.user_name || s.user_username || '–')}
                            {isMig && (
                              <span className="badge badge-grey" style={{ marginLeft: 6, fontSize: 9 }}>Migration</span>
                            )}
                          </div>
                          <div className="text-mono" style={{ fontWeight: 700, color: 'var(--copper)' }}>
                            {fmtNum(s.stunden)} h
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                          {s.rolle_bezeichnung && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.rolle_bezeichnung}</span>}
                          <span className={`badge badge-${typBadgeClass(s.typ)}`} style={{ fontSize: 10 }}>
                            {TYP_LABELS[s.typ] || s.typ}
                          </span>
                          {s.abrechenbar
                            ? <span className="badge badge-green" style={{ fontSize: 10 }}>abrechenbar</span>
                            : <span className="badge badge-grey" style={{ fontSize: 10 }}>nicht abrechenbar</span>}
                        </div>
                        {positionText && (
                          <div style={{ fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: 'var(--text-3)' }}>Position: </span>{positionText}
                          </div>
                        )}
                        {s.bemerkung && (
                          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.bemerkung}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {b.leistungen && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 10 }}>Leistungen des Tages</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>{b.leistungen}</div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <div className="card-title">Fotodokumentation ({fotos.length})</div>
          <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
            {uploading ? '⏳ Lädt hoch…' : '📷 Fotos hinzufügen'}
            <input type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={async e => { if (!e.target.files.length) return; await handleFotos(Array.from(e.target.files)); e.target.value = ''; }} />
          </label>
        </div>
        {fotos.length === 0 ? (
          <div className="empty"><div className="empty-icon">📷</div><h3>Noch keine Fotos</h3><p>Oben hinzufügen</p></div>
        ) : (
          <div className="photo-grid">
            {fotos.map(f => (
              <div key={f.id} className="photo-item" style={{ position: 'relative' }}>
                <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button onClick={() => deleteFoto(f.id)}
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(b.sig_ersteller || b.sig_bauleiter) && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Unterschriften</div>
          <div className="grid-2">
            {b.sig_ersteller && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600 }}>DV Elektro (Ausführender)</div>
                <img src={b.sig_ersteller} alt="" style={{ width: '100%', height: 80, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white' }} />
              </div>
            )}
            {b.sig_bauleiter && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600 }}>Bauleiter (Auftraggeber)</div>
                <img src={b.sig_bauleiter} alt="" style={{ width: '100%', height: 80, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white' }} />
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, margin: '24px 0' }}>
        <button className="btn btn-secondary" onClick={() => navigate(`/bautagebuch/${id}/bearbeiten`)}>✏️ Bearbeiten</button>
        <button className="btn btn-primary" style={{ padding: '12px 32px' }} onClick={exportPdf}>📄 Als PDF exportieren</button>
      </div>
    </div>
  );
}
